<template>
  <main class="multicolumn-view">
    <h1 class="view-full-span">
      {{ $t('general.greeting', { name: bowlerName }) }}
    </h1>

    <h1 v-if="isLoading" class="view-full-span busy-indicator-container">
      <BusyIndicator />
    </h1>

    <div v-else-if="error" class="error">
      <div>
        {{ $t('general.error-exclamation') }}
        <strong>{{ error }}</strong>
      </div>
    </div>

    <template v-else>
      <BowlerProfileSummary />
      <BowlerResultsSummary />
    </template>
  </main>
</template>

<script lang="ts" setup>
import { isAxiosError } from 'axios'
import isNil from 'lodash/isNil'
import { computed, onMounted, ref } from 'vue'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import BowlerProfileSummary from '@/components/widgets/bowler-app/BowlerProfileSummary.vue'
import BowlerResultsSummary from '@/components/widgets/bowler-app/BowlerResultsSummary.vue'
import { useBowlerStore } from '@/store/bowler-app/bowler'
import { useUserStore } from '@/store/bowler-app/user'
import { formatValue } from '@/utils/format'

const bowler = useBowlerStore()
const user = useUserStore()

/* local state */

const error = ref<string | null>()
const isLoading = ref<boolean>(false)

/* hooks */

onMounted(async (): Promise<void> => {
  isLoading.value = true

  try {
    await user.fetch()
    if (!isNil(user.bowlerId)) {
      await bowler.fetch(String(user.bowlerId))
    }
  } catch (e) {
    const message = isAxiosError(e)
      ? (e.response?.data?.detail as string)
      : (e as string)
    console.error(`Failed fetching bowler's data: ${message}`)
    error.value = message
  }

  isLoading.value = false
})

/* computed */

const bowlerName = computed<string>((): string =>
  formatValue(bowler.firstName ?? user.firstName),
)
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.busy-indicator-container {
  text-align: center;
}

.error {
  color: $color-danger;
}
</style>
